// RBAC Configuration - Role-Based Access Control
// This file contains all RBAC configuration constants

// ============================================
// PERMANENT DEVELOPER EMAIL (Server-side validated via Supabase Auth)
// This cannot be edited or deleted by anyone
// ============================================
export const DEVELOPER_EMAIL = 'aimanalishezanbusiness@gmail.com';

// ============================================
// ROLE DEFINITIONS
// ============================================
export const ROLES = {
  DEVELOPER: 'developer',  // Ultimate access - cannot be removed or downgraded
  ADMIN: 'admin',          // Full access except cannot modify developer
  MODERATOR: 'moderator',  // Limited access based on assigned permissions & campaigns
  USER: 'user'             // Basic read-only access (future use)
};

// Role hierarchy (higher number = higher privilege)
export const ROLE_HIERARCHY = {
  [ROLES.DEVELOPER]: 100,
  [ROLES.ADMIN]: 80,
  [ROLES.MODERATOR]: 40,
  [ROLES.USER]: 10
};

// ============================================
// PERMISSIONS DEFINITIONS
// ============================================
export const PERMISSIONS = {
  // Dashboard permissions
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_FULL_STATS: 'view_full_stats',
  
  // Lead permissions
  VIEW_LEADS: 'view_leads',
  EDIT_LEADS: 'edit_leads',
  DELETE_LEADS: 'delete_leads',
  
  // Call List permissions
  VIEW_CALL_LIST: 'view_call_list',
  MANAGE_CALL_LIST: 'manage_call_list',
  
  // Campaign permissions
  VIEW_CAMPAIGNS: 'view_campaigns',
  CREATE_CAMPAIGNS: 'create_campaigns',
  EDIT_CAMPAIGNS: 'edit_campaigns',
  DELETE_CAMPAIGNS: 'delete_campaigns',
  
  // Inbox permissions
  VIEW_INBOX: 'view_inbox',
  SEND_MESSAGES: 'send_messages',
  
  // Calendar & Bookings
  VIEW_CALENDAR: 'view_calendar',
  MANAGE_BOOKINGS: 'manage_bookings',
  
  // Automation permissions
  VIEW_AUTOMATION: 'view_automation',
  MANAGE_AUTOMATION: 'manage_automation',
  
  // Settings permissions
  VIEW_SETTINGS: 'view_settings',
  MANAGE_SETTINGS: 'manage_settings',
  
  // Admin permissions
  ACCESS_ADMIN_PANEL: 'access_admin_panel',
  MANAGE_USERS: 'manage_users',
  ASSIGN_ROLES: 'assign_roles',
  ASSIGN_CAMPAIGNS: 'assign_campaigns',
  
  // Chatbot permissions
  ACCESS_CHATBOT: 'access_chatbot',
  
  // Signal Engine permissions
  VIEW_SIGNALS: 'view_signals',
  MANAGE_SIGNALS: 'manage_signals',
  
  // Country Pack Intelligence permissions
  MANAGE_SOURCE_REGISTRY: 'manage_source_registry',
  
  // Web Tracking permissions
  VIEW_WEB_TRACKING: 'view_web_tracking',
  MANAGE_WEB_TRACKING: 'manage_web_tracking',
  
  // Delete permissions (controlled by admin)
  CAN_DELETE: 'can_delete'  // Controls ability to delete leads, campaigns, etc.
};

// ============================================
// DEFAULT PERMISSIONS BY ROLE
// ============================================
export const DEFAULT_ROLE_PERMISSIONS = {
  [ROLES.DEVELOPER]: Object.values(PERMISSIONS), // All permissions
  
  [ROLES.ADMIN]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_FULL_STATS,
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.EDIT_LEADS,
    PERMISSIONS.DELETE_LEADS,
    PERMISSIONS.VIEW_CALL_LIST,
    PERMISSIONS.MANAGE_CALL_LIST,
    PERMISSIONS.VIEW_CAMPAIGNS,
    PERMISSIONS.CREATE_CAMPAIGNS,
    PERMISSIONS.EDIT_CAMPAIGNS,
    PERMISSIONS.DELETE_CAMPAIGNS,
    PERMISSIONS.VIEW_INBOX,
    PERMISSIONS.SEND_MESSAGES,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.VIEW_AUTOMATION,
    PERMISSIONS.MANAGE_AUTOMATION,
    PERMISSIONS.VIEW_SETTINGS,
    PERMISSIONS.MANAGE_SETTINGS,
    PERMISSIONS.ACCESS_ADMIN_PANEL,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.ASSIGN_ROLES,
    PERMISSIONS.ASSIGN_CAMPAIGNS,
    PERMISSIONS.ACCESS_CHATBOT,  // Admins have chatbot access
    PERMISSIONS.CAN_DELETE,  // Admins have delete access by default
    PERMISSIONS.VIEW_SIGNALS,
    PERMISSIONS.MANAGE_SIGNALS,
    PERMISSIONS.MANAGE_SOURCE_REGISTRY,
    PERMISSIONS.VIEW_WEB_TRACKING,
    PERMISSIONS.MANAGE_WEB_TRACKING
  ],
  
  [ROLES.MODERATOR]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.EDIT_LEADS,
    PERMISSIONS.VIEW_CALL_LIST,
    PERMISSIONS.VIEW_CAMPAIGNS,
    PERMISSIONS.VIEW_INBOX,
    PERMISSIONS.SEND_MESSAGES,
    PERMISSIONS.VIEW_CALENDAR,
    PERMISSIONS.VIEW_SETTINGS,
    PERMISSIONS.ACCESS_CHATBOT,  // Moderators have chatbot by default
    PERMISSIONS.VIEW_SIGNALS,
    PERMISSIONS.VIEW_WEB_TRACKING
  ],
  
  [ROLES.USER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_LEADS,
    PERMISSIONS.VIEW_CALL_LIST,
    PERMISSIONS.VIEW_SETTINGS,
    PERMISSIONS.VIEW_SIGNALS
  ]
};

// ============================================
// PAGE ACCESS MAPPING
// Maps navigation items to required permissions
// ============================================
export const PAGE_PERMISSIONS = {
  dashboard: PERMISSIONS.VIEW_DASHBOARD,
  leads: PERMISSIONS.VIEW_LEADS,
  calllist: PERMISSIONS.VIEW_CALL_LIST,
  campaigns: PERMISSIONS.VIEW_CAMPAIGNS,
  calendar: PERMISSIONS.VIEW_CALENDAR,
  bookings: PERMISSIONS.MANAGE_BOOKINGS,
  inbox: PERMISSIONS.VIEW_INBOX,
  automation: PERMISSIONS.VIEW_AUTOMATION,
  settings: PERMISSIONS.VIEW_SETTINGS,
  'linkedin-automation': PERMISSIONS.VIEW_AUTOMATION,
  admin: PERMISSIONS.ACCESS_ADMIN_PANEL,
  signals: PERMISSIONS.VIEW_SIGNALS,
  'web-tracking': PERMISSIONS.VIEW_WEB_TRACKING
};

// ============================================
// ALL AVAILABLE PAGES (for page access control)
// ============================================
export const ALL_PAGES = [
  { key: 'dashboard', name: 'Dashboard', description: 'View statistics and analytics' },
  { key: 'leads', name: 'Leads', description: 'View and manage leads' },
  { key: 'calllist', name: 'Call List', description: 'View and manage call list' },
  { key: 'campaigns', name: 'Campaigns', description: 'View campaigns' },
  { key: 'calendar', name: 'Calendar', description: 'View calendar' },
  { key: 'bookings', name: 'Bookings', description: 'Manage bookings' },
  { key: 'inbox', name: 'Inbox', description: 'View and send messages' },
  { key: 'automation', name: 'Automation', description: 'Email automation' },
  { key: 'linkedin-automation', name: 'LinkedIn', description: 'LinkedIn automation' },
  { key: 'settings', name: 'Settings', description: 'App settings' },
  { key: 'signals', name: 'Signals', description: 'Investment signal call list' },
  { key: 'web-tracking', name: 'Web Tracking', description: 'Website visitor tracking & analytics' }
];

// Default pages for each role
export const DEFAULT_ROLE_PAGES = {
  [ROLES.DEVELOPER]: ALL_PAGES.map(p => p.key), // All pages
  [ROLES.ADMIN]: ALL_PAGES.map(p => p.key), // All pages
  [ROLES.MODERATOR]: ['dashboard', 'leads', 'calllist', 'inbox', 'signals', 'web-tracking'], // Limited pages
  [ROLES.USER]: ['dashboard', 'leads', 'calllist', 'signals'] // Basic access + signals for profile users
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if email is the permanent developer
 */
export const isDeveloperEmail = (email) => {
  return email?.toLowerCase() === DEVELOPER_EMAIL.toLowerCase();
};

/**
 * Check if a role can modify another role
 * A role can only modify roles with lower privilege
 */
export const canModifyRole = (actorRole, targetRole) => {
  if (!actorRole || !targetRole) return false;
  
  const actorLevel = ROLE_HIERARCHY[actorRole] || 0;
  const targetLevel = ROLE_HIERARCHY[targetRole] || 0;
  
  return actorLevel > targetLevel;
};

/**
 * Get role display name
 */
export const getRoleDisplayName = (role) => {
  const names = {
    [ROLES.DEVELOPER]: 'Developer (Ultimate)',
    [ROLES.ADMIN]: 'Administrator',
    [ROLES.MODERATOR]: 'Moderator',
    [ROLES.USER]: 'User'
  };
  return names[role] || role;
};

/**
 * Get role badge color
 */
export const getRoleBadgeColor = (role) => {
  const colors = {
    [ROLES.DEVELOPER]: 'bg-purple-100 text-purple-800 border-purple-300',
    [ROLES.ADMIN]: 'bg-red-100 text-red-800 border-red-300',
    [ROLES.MODERATOR]: 'bg-blue-100 text-blue-800 border-blue-300',
    [ROLES.USER]: 'bg-gray-100 text-gray-800 border-gray-300'
  };
  return colors[role] || 'bg-gray-100 text-gray-800 border-gray-300';
};

/**
 * Check if user can view activity tracking (admin or developer only)
 */
export const canViewActivityTracking = (email, role) => {
  if (isDeveloperEmail(email)) return true;
  if (role === ROLES.ADMIN || role === ROLES.DEVELOPER) return true;
  return false;
};
